import React, { useState, useRef } from 'react';
import { LayoutDashboard, Users, Settings, ChevronRight, ArrowLeft, ChevronDown, Check, LogOut, BookOpen, FileText, UserCog, ClipboardCheck, GraduationCap } from 'lucide-react';
import { type Classroom } from '../../accounts';
import { DjayNarrator } from '../../mascot';
import { useClickOutside } from '../../app/hooks/useClickOutside';
import { useEscapeKey } from '../../app/hooks/useEscapeKey';
import { CoBadge } from './components/CoBadge';
export type TeacherV2Route = 'today' | 'class' | 'homework' | 'subjects' | 'assessments' | 'reports' | 'management' | 'settings';
type NavGroup = 'Vandaag' | 'Mijn klas' | 'Inzicht' | 'Beheer';
interface NavItem {
  id: TeacherV2Route;
  label: string;
  icon: React.ReactNode;
  group: NavGroup;
}
const NAV_ITEMS: NavItem[] = [{
  id: 'today',
  label: 'Vandaag',
  icon: <LayoutDashboard className="w-5 h-5" data-id="element-9797" />,
  group: 'Vandaag'
}, {
  id: 'class',
  label: 'Mijn Klas',
  icon: <Users className="w-5 h-5" data-id="element-9798" />,
  group: 'Mijn klas'
}, {
  id: 'homework',
  label: 'Huiswerk',
  icon: <GraduationCap className="w-5 h-5" data-id="element-9799" />,
  group: 'Mijn klas'
}, {
  id: 'assessments',
  label: 'Toetsen',
  icon: <ClipboardCheck className="w-5 h-5" data-id="element-9800" />,
  group: 'Mijn klas'
}, {
  id: 'subjects',
  label: 'Vakken',
  icon: <BookOpen className="w-5 h-5" data-id="element-9801" />,
  group: 'Inzicht'
}, {
  id: 'reports',
  label: 'Rapporten',
  icon: <FileText className="w-5 h-5" data-id="element-9802" />,
  group: 'Inzicht'
}, {
  id: 'management',
  label: 'Klas Beheer',
  icon: <UserCog className="w-5 h-5" data-id="element-9803" />,
  group: 'Beheer'
}];
interface TeacherSidebarV2Props {
  activeRoute: TeacherV2Route;
  onNavigate: (route: TeacherV2Route) => void;
  onLogout?: () => void;
  onReturnHome?: () => void;
  classroomName?: string;
  classrooms?: Classroom[];
  selectedClassroomId?: string | null;
  onSelectClassroom?: (id: string | null) => void;
  todayAttentionCount?: number;
  /** Active teacher account id — used to mark co-teacher classrooms with a 'Co' badge. */
  activeTeacherId?: string;
}
export function TeacherSidebarV2({
  activeRoute,
  onNavigate,
  onLogout,
  onReturnHome,
  classroomName = 'Mijn Klas',
  classrooms = [],
  selectedClassroomId = null,
  onSelectClassroom,
  todayAttentionCount = 0,
  activeTeacherId
}: TeacherSidebarV2Props) {
  const [collapsed, setCollapsed] = useState(false);
  const [showClassroomDropdown, setShowClassroomDropdown] = useState(false);
  const classroomDropdownRef = useRef<HTMLDivElement>(null);
  useClickOutside(classroomDropdownRef, () => {
    if (showClassroomDropdown) setShowClassroomDropdown(false);
  });
  useEscapeKey(() => {
    if (showClassroomDropdown) setShowClassroomDropdown(false);
  });
  const activeClassroom = selectedClassroomId ? classrooms.find(c => c.id === selectedClassroomId) : null;
  const displayClassName = activeClassroom ? activeClassroom.name : classrooms.length > 0 ? 'Alle klassen' : classroomName;
  const isCoTeach = (c: Classroom) => !!activeTeacherId && c.teacherId !== activeTeacherId;
  const activeIsCoTeach = activeClassroom ? isCoTeach(activeClassroom) : false;
  return <aside className={`flex flex-col bg-white border-r border-slate-200 transition-all duration-300 flex-shrink-0 h-full ${collapsed ? 'w-20' : 'w-72'}`} data-id="element-9804">
      {/* Header / Logo */}
      <div className={`flex items-center border-b border-slate-100 flex-shrink-0 h-20 ${collapsed ? 'justify-center px-2' : 'justify-between px-6'}`} data-id="element-9805">
        {!collapsed && <div className="flex items-center gap-3" data-id="element-9806">
            <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center border border-indigo-100 shadow-sm overflow-hidden" data-id="element-9807">
              <DjayNarrator size="xs" emotion="happy" data-id="element-9808" />
            </div>
            <div className="flex flex-col" data-id="element-9809">
              <span className="text-sm font-black text-slate-900 leading-tight tracking-tight" data-id="element-9810">
                Meester djay
              </span>
              <span className="text-xs font-bold text-indigo-600 uppercase tracking-wider" data-id="element-9811">
                Docentportaal
              </span>
            </div>
          </div>}
        {collapsed && <div className="relative w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center border border-indigo-100 shadow-sm overflow-hidden group" data-id="element-9812">
            <DjayNarrator size="xs" emotion="happy" data-id="element-9813" />
            {activeClassroom && <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-indigo-600 text-white text-[8px] font-bold rounded-full flex items-center justify-center border border-white" title={activeClassroom.name} data-id="element-9814">
                {activeClassroom.groupLevel}
              </div>}
          </div>}
        {!collapsed && <button onClick={() => setCollapsed(true)} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:outline-none" aria-label="Inklappen" data-id="element-9815">
            <ChevronRight className="w-4 h-4 rotate-180" data-id="element-9816" />
          </button>}
      </div>

      {/* Classroom Selector */}
      {!collapsed && classrooms.length > 0 && onSelectClassroom && <div ref={classroomDropdownRef} className="px-4 py-4 border-b border-slate-100 relative" data-id="element-9817">
          <button onClick={() => setShowClassroomDropdown(!showClassroomDropdown)} className="w-full flex items-center justify-between bg-white hover:bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm" data-id="element-9818">
            <div className="flex flex-col items-start overflow-hidden" data-id="element-9819">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5" data-id="element-9820">
                Huidige weergave
              </span>
              <div className="flex items-center gap-2 w-full" data-id="element-9821">
                <span className="text-sm font-bold text-slate-900 truncate text-left" data-id="element-9822">
                  {displayClassName}
                </span>
                {activeIsCoTeach && <CoBadge data-id="element-9823" />}
              </div>
            </div>
            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showClassroomDropdown ? 'rotate-180' : ''}`} data-id="element-9824" />
          </button>

          {showClassroomDropdown && <div className="absolute top-full left-4 right-4 mt-2 bg-white border border-slate-200 rounded-xl shadow-lg z-20 overflow-hidden py-1.5" data-id="element-9825">
              <button onClick={() => {
          onSelectClassroom(null);
          setShowClassroomDropdown(false);
        }} className={`w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors ${selectedClassroomId === null ? 'text-indigo-700 font-bold bg-indigo-50/50' : 'text-slate-700 font-medium'}`} data-id="element-9826">
                <span data-id="element-9827">Alle klassen</span>
                {selectedClassroomId === null && <Check className="w-4 h-4" data-id="element-9828" />}
              </button>
              <div className="h-px bg-slate-100 my-1.5 mx-4" data-id="element-9829" />
              {classrooms.map(c => <button key={c.id} onClick={() => {
          onSelectClassroom(c.id);
          setShowClassroomDropdown(false);
        }} className={`w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors ${selectedClassroomId === c.id ? 'text-indigo-700 font-bold bg-indigo-50/50' : 'text-slate-700 font-medium'}`} data-id="element-9830">
                  <div className="flex items-center gap-2 truncate min-w-0" data-id="element-9831">
                    <span className="truncate" data-id="element-9832">{c.name}</span>
                    {isCoTeach(c) && <CoBadge data-id="element-9833" />}
                    <span className="text-[10px] py-0.5 px-2 bg-slate-100 text-slate-500 rounded-md font-bold flex-shrink-0" data-id="element-9834">
                      {c.learnerIds.length}
                    </span>
                  </div>
                  {selectedClassroomId === c.id && <Check className="w-4 h-4 flex-shrink-0" data-id="element-9835" />}
                </button>)}
            </div>}
        </div>}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-6 px-3" data-id="element-9836">
        <div className="space-y-1.5" data-id="element-9837">
          {(() => {
          const groups: NavGroup[] = ['Vandaag', 'Mijn klas', 'Inzicht', 'Beheer'];
          return groups.map((group, index) => {
            const groupItems = NAV_ITEMS.filter(item => item.group === group);
            if (groupItems.length === 0) return null;
            // "Vandaag" is a single item — no group header, no extra divider
            const showHeader = group !== 'Vandaag';
            return <div key={group} className={index > 0 ? 'pt-2' : ''} data-id="element-9838">
                  {index > 0 && <div className="h-px bg-slate-100 my-3 mx-2" data-id="element-9839" />}
                  {!collapsed && showHeader && <div className="px-4 mb-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-9840">
                      {group}
                    </div>}
                  <div className="space-y-1.5" data-id="element-9841">
                    {groupItems.map(item => {
                  const isActive = activeRoute === item.id;
                  const badgeCount = item.id === 'today' ? todayAttentionCount : 0;
                  return <button key={item.id} onClick={() => onNavigate(item.id)} title={collapsed ? item.label : undefined} className={`w-full flex items-center gap-3 rounded-xl transition-all duration-200 relative focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:outline-none ${collapsed ? 'justify-center px-2 py-3' : 'px-4 py-3'} ${isActive ? 'bg-indigo-50 text-indigo-700 font-bold shadow-sm border border-indigo-100/50' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 font-medium border border-transparent'}`} data-id="element-9842">
                          <span className={`relative flex-shrink-0 transition-colors ${isActive ? 'text-indigo-600' : 'text-slate-400 group-hover:text-slate-600'}`} data-id="element-9843">
                            {item.icon}
                            {badgeCount > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 bg-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none" data-id="element-9844">
                                {badgeCount > 9 ? '9+' : badgeCount}
                              </span>}
                          </span>
                          {!collapsed && <span className="text-sm truncate" data-id="element-9845">
                              {item.label}
                            </span>}
                        </button>;
                })}
                  </div>
                </div>;
          });
        })()}
        </div>
      </nav>

      {/* Footer */}
      <div className={`flex-shrink-0 border-t border-slate-100 ${collapsed ? 'p-3' : 'p-5'} space-y-2`} data-id="element-9846">
        {collapsed && <button onClick={() => setCollapsed(false)} className="w-full flex items-center justify-center p-2.5 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors mb-2" aria-label="Uitklappen" data-id="element-9847">
            <ChevronRight className="w-5 h-5" data-id="element-9848" />
          </button>}

        <button onClick={() => onNavigate('settings')} title={collapsed ? 'Instellingen' : undefined} className={`w-full flex items-center gap-3 rounded-xl transition-colors focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:outline-none ${collapsed ? 'justify-center p-2.5' : 'px-4 py-2.5'} ${activeRoute === 'settings' ? 'bg-indigo-50 text-indigo-700 font-bold' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900 font-medium'}`} data-id="element-9849">
          <Settings className={`w-5 h-5 ${activeRoute === 'settings' ? 'text-indigo-600' : ''}`} data-id="element-9850" />
          {!collapsed && <span className="text-sm" data-id="element-9851">Instellingen</span>}
        </button>

        {onLogout && <button onClick={onLogout} className={`w-full flex items-center gap-3 rounded-xl text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-colors focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:outline-none ${collapsed ? 'justify-center p-2.5' : 'px-4 py-2.5 font-medium'}`} title={collapsed ? 'Uitloggen' : undefined} data-id="element-9852">
            <LogOut className="w-5 h-5" data-id="element-9853" />
            {!collapsed && <span className="text-sm" data-id="element-9854">Uitloggen</span>}
          </button>}

        {onReturnHome && <button onClick={onReturnHome} className={`w-full flex items-center gap-3 rounded-xl text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-colors focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:outline-none ${collapsed ? 'justify-center p-2.5' : 'px-4 py-2.5 font-medium'}`} title={collapsed ? 'Naar leerlingomgeving' : undefined} data-id="element-9855">
            <ArrowLeft className="w-5 h-5" data-id="element-9856" />
            {!collapsed && <span className="text-sm" data-id="element-9857">Leerlingomgeving</span>}
          </button>}
      </div>
    </aside>;
}